// src/context/AuthContext.js
//
// Wraps the whole app. Gives every component access to:
//   const { user, isPaid, loading } = useAuth();
//
// Wrap your app in App.js:
//   import { AuthProvider } from './context/AuthContext';
//   <AuthProvider><App /></AuthProvider>

import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser   ] = useState(null);
  const [isPaid,  setIsPaid ] = useState(false);
  const [loading, setLoading] = useState(true); // true while checking session

  useEffect(() => {
    // Check if user is already logged in on page load
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchPaidStatus(session.user.id);
      else setLoading(false);
    });

    // Listen for login/logout events anywhere in the app
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchPaidStatus(session.user.id);
      else { setIsPaid(false); setLoading(false); }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Check Supabase database: has this user paid?
  async function fetchPaidStatus(userId) {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('is_paid')
        .eq('id', userId)
        .single();
      setIsPaid(data?.is_paid === true);
    } catch {
      setIsPaid(false);
    } finally {
      setLoading(false);
    }
  }

  // Call this after successful payment to unlock premium
  async function markAsPaid() {
    if (!user) return;
    await supabase
      .from('profiles')
      .upsert({ id: user.id, is_paid: true });
    setIsPaid(true);
  }

  async function signOut() {
    await supabase.auth.signOut();
    setUser(null);
    setIsPaid(false);
  }

  return (
    <AuthContext.Provider value={{ user, isPaid, loading, markAsPaid, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}